import React from 'react';
import styled from 'styled-components';

import { Skeleton } from '@material-ui/lab';

import { VenuePlan } from '../../graphql/API';
import { useAdminGetDashboardVenuesStatsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardVenuesStatsDataQuery';
import { calculatePercentageChange } from '../../utils/dashboard';
import { VENUE_MANUAL_TAGS } from '../../utils/manual-tags';
import { withMarket } from './dashboardUtils';
import ActiveCard from './ActiveCard';
import { CardError } from './CardError';
import { PackageCard } from './PackageCard';

// Backend totals include the plans assigned for free by the admin; show the real paid count
const paidCount = (total?: number | null, assignedByAdmin?: number | null) =>
  String(Math.max((total || 0) - (assignedByAdmin || 0), 0));

type Props = {
  fromDate: string | null;
  priorFrom: string | null;
  toDate: string | null;
  priorTo: string | null;
  selectedLabel: string;
  marketID: string | null;
};

export const VenuesV2: React.FC<Props> = ({
  fromDate,
  priorFrom,
  priorTo,
  toDate,
  selectedLabel,
  marketID,
}) => {
  const { data, isFetching, isError, refetch } = useAdminGetDashboardVenuesStatsDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
    marketID: marketID || '',
  });

  return (
    <PackageContainerDesktopVenueSection>
      {isFetching && (
        <>
          <Skeleton
            animation="wave"
            variant="rect"
            width={'100%'}
            height={100}
            style={{ borderRadius: '8px' }}
          />
          <Skeleton
            animation="wave"
            variant="rect"
            width={'100%'}
            height={100}
            style={{ borderRadius: '8px' }}
          />
          <Skeleton
            animation="wave"
            variant="rect"
            width={'100%'}
            height={100}
            style={{ borderRadius: '8px' }}
          />
          <Skeleton
            animation="wave"
            variant="rect"
            width={'100%'}
            height={100}
            style={{ borderRadius: '8px' }}
          />
        </>
      )}
      {isError && !isFetching && (
        <FullRow>
          <CardError onRetry={() => refetch()} />
        </FullRow>
      )}
      {!isFetching && data ? (
        <>
          <ActiveCard
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#E6EAFD"
            count={String(data?.totalActiveVenues)}
            label="Active venues"
            link={withMarket(
              `/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}`,
              marketID,
            )}
            comparison={calculatePercentageChange(
              data?.totalCurrentActiveVenues || 0,
              data?.totalPriorActiveVenues || 0,
            )}
          />
          <PackageCard
            link={withMarket(
              `/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.free}`,
              marketID,
            )}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={String(data?.totalFreeVenuesWithoutOffers)}
            label="Free"
            newCount={data?.totalNewFreeVenues || 0}
            canceledCount={data?.totalExpiredFreeVenues || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentFreeVenuesWithoutOffers || 0,
              data?.totalPriorFreeVenuesWithoutOffers || 0,
            )}
          />
          <PackageCard
            link={withMarket(
              `/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.starter}`,
              marketID,
            )}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={paidCount(data?.totalStarterVenuesWithoutOffers, data?.totalStarterFreeVenues)}
            label="Starter"
            newCount={data?.totalNewStarterVenues || 0}
            canceledCount={data?.totalExpiredStarterVenues || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentStarterVenuesWithoutOffers || 0,
              data?.totalPriorStarterVenuesWithoutOffers || 0,
            )}
          />
          <PackageCard
            link={withMarket(
              `/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.plus}`,
              marketID,
            )}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={paidCount(data?.totalPlusVenuesWithoutOffers, data?.totalPlusFreeVenues)}
            label="Plus"
            newCount={data?.totalNewPlusVenues || 0}
            canceledCount={data?.totalExpiredPlusVenues || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentPlusVenuesWithoutOffers || 0,
              data?.totalPriorPlusVenuesWithoutOffers || 0,
            )}
          />
          <ActiveCard
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#EEE2F8"
            count={String(data?.totalActiveBrands)}
            label="Active brands subscribed"
            link={withMarket(`/businesses?tags=${VENUE_MANUAL_TAGS.Online}`, marketID)}
            comparison={calculatePercentageChange(
              data?.totalCurrentActiveBrands || 0,
              data?.totalPriorActiveBrands || 0,
            )}
          />
          <PackageCard
            link={withMarket(
              `/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.basic}`,
              marketID,
            )}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={paidCount(data?.totalBasicBrandsWithoutOffers, data?.totalBasicFreeBrands)}
            label="Basic"
            newCount={data?.totalNewBasicBrands || 0}
            canceledCount={data?.totalExpiredBasicBrands || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentBasicBrandsWithoutOffers || 0,
              data?.totalPriorBasicBrandsWithoutOffers || 0,
            )}
          />
          <PackageCard
            link={withMarket(
              `/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.starter}`,
              marketID,
            )}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={paidCount(data?.totalStarterBrandsWithoutOffers, data?.totalStarterFreeBrands)}
            label="Starter"
            newCount={data?.totalNewStarterBrands || 0}
            canceledCount={data?.totalExpiredStarterBrands || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentStarterBrandsWithoutOffers || 0,
              data?.totalPriorStarterBrandsWithoutOffers || 0,
            )}
          />
          <PackageCard
            link={withMarket(
              `/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.plus}`,
              marketID,
            )}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={paidCount(data?.totalPlusBrandsWithoutOffers, data?.totalPlusFreeBrands)}
            label="Plus"
            newCount={data?.totalNewPlusBrands || 0}
            canceledCount={data?.totalExpiredPlusBrands || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentPlusBrandsWithoutOffers || 0,
              data?.totalPriorPlusBrandsWithoutOffers || 0,
            )}
          />
        </>
      ) : null}
    </PackageContainerDesktopVenueSection>
  );
};

const PackageContainerDesktopVenueSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 16px;
  padding-top: 80px;
  padding-bottom: 60px;
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;
